import { Component, Mixins, Prop, PropSync, Ref, Watch } from 'vue-property-decorator'
import AlertBanner from '@/components/common/AlertBanner/alert-banner.vue'
import LiquorTree from 'liquor-tree'
import StudyRequestPriorityDot from '@/modules/clinicalModule/components/StatusRequestPriorityDot.vue'
import { PortionOperationGuardMixin } from '@/api/operations/mixins/guards/portion-operation-guard.mixin'
import {
    AssaysViewNode,
    AssaysViewPortionNode,
    AssaysViewSampleNode,
    AssaysViewSampleNodeStructure,
    AssaysViewTypeEnum
} from '@/components/assays-view/components/modal/assays-view-modal.model'
import { BModal } from 'bootstrap-vue'
import { CommonPriorityResponse } from '@/api/services/common/priority/common-priority.dto'
import sessionStore from '@/session/sessionStore'
import { AssaysViewPortionResponse } from '@/components/assays-view/services/assays-view-portion/assays-view-portion.dto'
import { cloneDeep } from 'lodash'
import { PortionTypeEnum } from '@/api/services/common/portion-type/common-portion-type.dto'
import {
    AssaysViewBatchModalState,
    BatchItemWrapper,
    initialAssaysViewBatchModalState
} from '@/components/assays-view/components/batch/batch-modal/assays-view-batch-modal.model'

@Component({
    components: {
        AlertBanner,
        LiquorTree,
        StudyRequestPriorityDot
    }
})
export default class AssaysViewBatchModal<T>
    extends Mixins<PortionOperationGuardMixin>(PortionOperationGuardMixin) {
    @PropSync('selectedPortions') syncedSelectedPortions!: AssaysViewPortionResponse[]

    @Prop({ type: String, required: true }) readonly qaId!: string
    @Prop({ type: String }) readonly modalClass!: string

    @Prop({ type: Number, required: true }) readonly casId!: number
    @Prop({ type: Object }) readonly casePriority!: CommonPriorityResponse
    @Prop({ type: Array, default: () => [] }) readonly sampleNodes!: AssaysViewSampleNodeStructure[]

    @Prop({ type: String, required: true }) readonly modalTitle!: string
    @Prop({ type: String, required: true }) readonly toolbarTitle!: string
    @Prop({ type: String, required: true }) readonly filterMessage!: string
    @Prop({ type: String, required: true }) readonly selectionMessage!: string
    @Prop({ type: String, required: true }) readonly helpMessage!: string
    @Prop({ type: String, required: true }) readonly actionMessage!: string

    @Prop({ type: Array, default: () => [] }) readonly batchItems!: T[]
    @Prop({ type: Boolean, default: false }) readonly batchItemLoading!: boolean
    @Prop({ type: Function, required: true }) readonly batchItemIdResolver!: (batchItem: T) => string
    @Prop({ type: Function, required: true }) readonly batchItemNameResolver!: (batchItem: T) => string
    @Prop({ type: Boolean, default: false }) readonly batchItemFilterDisabled!: boolean
    @Prop({ type: Function }) readonly batchItemFilter!: (batchItems: T[], filter: string) => T[]
    @Prop({ type: Function, required: true }) readonly actionFn!: (selectedBatchItemWrappers: BatchItemWrapper<T>[]) => Promise<void>

    @Ref() readonly batchModal!: BModal
    @Ref('tree') readonly tree!: LiquorTree

    state: AssaysViewBatchModalState<T>

    constructor() {
        super()
        this.state = initialAssaysViewBatchModalState<T>()
    }

    @Watch('batchItemLoading')
    async onBatchItemLoadingChange(): Promise<void> {
        this.state.batchItemLoading = this.batchItemLoading
    }

    get priorities(): CommonPriorityResponse[] {
        const priorities: CommonPriorityResponse[] = sessionStore.state.priorities
        return priorities.filter(priority => priority.active)
    }

    get portionTypeEnum(): typeof PortionTypeEnum {
        return PortionTypeEnum
    }

    get visibleBatchItemWrappers(): boolean {
        return this.syncedSelectedPortions.some(selectedPortion => this.$_canBeAssayRequestedToPortion(selectedPortion))
    }
    get batchItemWrappers(): BatchItemWrapper<T>[] {
        let batchItemsFiltered: T[]
        if (this.batchItemFilter) {
            batchItemsFiltered = this.batchItemFilter(this.batchItems, this.state.batchItemFilter)
        } else {
            batchItemsFiltered =
                this.batchItems.filter(batchItem => {
                    const batchItemName = this.batchItemNameResolver(batchItem).toLowerCase()
                    const filter = this.state.batchItemFilter.toLowerCase()
                    return batchItemName.includes(filter)
                })
        }
        return batchItemsFiltered.map(batchItem => ({ batchItem } as BatchItemWrapper<T>))
    }

    get visibleBatchItemFilter(): boolean {
        return this.visibleBatchItemWrappers
    }

    get actionDisabled(): boolean {
        return !this.syncedSelectedPortions.length ||
            !this.state.selectedBatchItemWrappers.length
    }

    /// OPERATIONS ///

    private _initialize(): void {
        this.state.priorityGlobal = this.casePriority
        if (!this.state.priorityGlobal && this.priorities.length) {
            this.state.priorityGlobal = this.priorities[0]
        }

        this.state.sampleNodes = cloneDeep(this.sampleNodes)
        for (const sampleNode of this.state.sampleNodes) {
            if (sampleNode.children) {
                for (const child of sampleNode.children) {
                    child.children = []
                }
            }
        }

        this.state.loaded = true
    }

    countSelectablePortionNodes(portionNodes: AssaysViewPortionNode[]): number {
        return portionNodes.filter(portionNode => !portionNode.states.disabled).length
    }
    countSelectedPortionNodes(portionNodes: AssaysViewPortionNode[]): number {
        return portionNodes.filter(portionNode => portionNode.checked()).length
    }

    changePriority(batchItemWrapper: BatchItemWrapper<T>, priority: CommonPriorityResponse): void {
        batchItemWrapper.priority = priority

        this.$root.$emit('bv::hide::popover')
    }
    changePriorityGlobal(priority: CommonPriorityResponse): void {
        this.state.priorityGlobal = priority

        for (const selectedBatchItem of this.state.selectedBatchItemWrappers) {
            selectedBatchItem.priority = priority
        }

        this.$root.$emit('bv::hide::popover')
    }

    addBatchItem(batchItem: T): void {
        if (batchItem && this.state.priorityGlobal) {
            const batchItemWrapper: BatchItemWrapper<T> = {
                batchItem,
                priority: this.state.priorityGlobal
            }
            this.state.selectedBatchItemWrappers.push(batchItemWrapper)
        }
    }
    removeBatchItem(index: number): void {
        this.state.selectedBatchItemWrappers.splice(index, 1)
    }
    removeAllBatchItems(): void {
        this.state.selectedBatchItemWrappers = []
    }

    /// TREE ///

    initializeTreeState(): void {
        const sampleSelection = this.tree.findAll<AssaysViewSampleNode>(AssaysViewTypeEnum.SAMPLE)
        if (sampleSelection) {
            const selectedPortionIds = this.syncedSelectedPortions.map(selectedPortion => selectedPortion.portionId)
            for (const sampleNode of sampleSelection) {
                for (const portionNode of sampleNode.children) {
                    if (!this.$_canBeAssayRequestedToPortion(portionNode.data)) {
                        portionNode.disable()
                    } else if (selectedPortionIds.includes(portionNode.data.portionId)) {
                        portionNode.check()
                        sampleNode.expand()
                    }
                }

                const mustDisableSampleNode =
                    sampleNode.children.every(portionNode => !this.$_canBeAssayRequestedToPortion(portionNode.data))
                if (mustDisableSampleNode) {
                    sampleNode.disable()
                }
            }
        }
    }

    private _handlePortionNode(portionNode: AssaysViewPortionNode, checked: boolean): void {
        if (this.$_canBeAssayRequestedToPortion(portionNode.data)) {
            portionNode.states.checked = checked

            const porIndex =
                this.syncedSelectedPortions.findIndex(
                    selectedPortion => selectedPortion.portionId === portionNode.data.portionId)
            if (checked) {
                if (porIndex < 0) {
                    this.syncedSelectedPortions.push(portionNode.data)
                }
            } else {
                if (porIndex >= 0) {
                    this.syncedSelectedPortions.splice(porIndex, 1)
                }

                if (!this.syncedSelectedPortions.length) {
                    this.state = initialAssaysViewBatchModalState<T>()
                    this._initialize()
                }
            }
        }
    }
    private static _updateSampleNodeCheckState(sampleNode: AssaysViewSampleNode, checked: boolean): void {
        if (checked) {
            if (!sampleNode.children?.some(child => !child.states.disabled && !child.checked())) {
                sampleNode.states.checked = true
                sampleNode.expand()
            }
        } else {
            sampleNode.states.checked = false
        }
    }
    async onNodeCheck(node: AssaysViewNode): Promise<void> {
        switch (node.text) {
        case AssaysViewTypeEnum.SAMPLE:
            const sampleNode = node as AssaysViewSampleNode
            for (const portionNode of sampleNode.children) {
                this._handlePortionNode(portionNode, node.checked())
            }
            AssaysViewBatchModal._updateSampleNodeCheckState(sampleNode, node.checked())
            break
        case AssaysViewTypeEnum.PORTION:
            const portionNode = node as AssaysViewPortionNode
            this._handlePortionNode(portionNode, node.checked())
            if (portionNode.parent) {
                AssaysViewBatchModal._updateSampleNodeCheckState(portionNode.parent, node.checked())
            }
            break
        }
    }

    /// MODAL ///

    async show(): Promise<void> {
        this.state = initialAssaysViewBatchModalState<T>()
        this._initialize()
        this.batchModal.show()
    }
    hide(): void {
        this.batchModal.hide()
    }
}
